/**
 * Every read of server state, and every non-turn write.
 *
 * `queryKeys` is one object on purpose: every per-story key starts with the same
 * `['story', id]` prefix, so the commit transition can be precise about what moved. A client with one
 * refetched the entire `FullStory` after every action because it had no way to say "just
 * this part".
 *
 * The turn itself is not here. It is a stream, it is not idempotent, and it belongs to
 * `src/turn/` (D3: the stream writes to Zustand while it runs and commits into *this* cache
 * exactly once, on the terminal `chapter` event).
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/api/client';
import type {
  ChapterPage,
  FeedbackRequest,
  Profile,
  StoryCreate,
  StoryCreated,
} from '@/api/generated';
import type { UiKey } from '@/i18n/en.generated';

/**
 * How many chapters a page of `GET /chapters` carries.
 *
 * **Small on purpose, and it is a reading decision rather than a bandwidth one.** A cold load
 * lands the reader at the *end* of the story (`useOpenAtEnd`), which is the only place a
 * returning player wants to be — so everything above the fold on that first paint is scenery,
 * and forty chapters of it is forty chapters of prose fetched, parsed and laid out before the
 * one paragraph they came back for can be positioned. Five is roughly a screen or two of
 * scrollback above the newest chapter; the rest arrives as they climb, on the `before` cursor
 * the sentinel already drives.
 *
 * It is also what makes the paging real in the fixtures: the seeded story is 24 chapters, so
 * `mocks/` exercises five pages and a `next_before: null` rather than one page and a guess.
 */
export const CHAPTER_PAGE_SIZE = 5;

export const queryKeys = {
  profile: () => ['profile'] as const,
  translations: (locale: string) => ['translations', locale] as const,
  stories: () => ['stories'] as const,
  /** Prefix of everything below it — invalidating this invalidates state and chapters too. */
  story: (storyId: string) => ['story', storyId] as const,
  storyState: (storyId: string) => ['story', storyId, 'state'] as const,
  chapters: (storyId: string) => ['story', storyId, 'chapters'] as const,
};

// An `invalidateAfterTurn()` used to live here that invalidated the whole `['story', id]`
// prefix. It was deleted: the terminal `chapter` event already carries the chapter, the
// choices and the post-turn character snapshot (P1), so blanket invalidation would refetch
// exactly what the turn just spent ~50 seconds delivering. `turn/commit.ts` writes those
// three caches and invalidates only what the event does not carry.

// --- profile ----------------------------------------------------------------------------

/**
 * `enabled` exists for one caller: `AppI18nProvider` sits above the route guard, so it asks
 * for the profile on `/login` and `/leaderboard` too — both readable signed out — and
 * `GET /me` there is a guaranteed 401. Every other caller is inside `RequireAuth` and wants
 * the default.
 */
export function useProfile(enabled = true) {
  return useQuery({
    queryKey: queryKeys.profile(),
    queryFn: ({ signal }) => request('get', '/me', { signal }),
    enabled,
  });
}

/**
 * P7's whole point: the client can disable "new story" *before* the player fills in a form
 * and gets a 403. Undefined while the profile is loading — callers render neither state.
 */
export interface StoryBudget {
  used: number;
  max: number;
  remaining: number;
  canCreate: boolean;
}

export function useStoryBudget(): StoryBudget | undefined {
  const { data } = useProfile();
  if (!data) return undefined;
  const remaining = Math.max(0, data.max_stories_allowed - data.stories_used);
  return {
    used: data.stories_used,
    max: data.max_stories_allowed,
    remaining,
    // Slots are not the only thing that can refuse creation: the server runs the play gates
    // there too, because a story you cannot open is a dead end. `remaining` stays a pure
    // slot count — it is what the "N of M" line reads — and only `canCreate` folds in the
    // gate, so the two never disagree about which sentence to show.
    canCreate: remaining > 0 && data.is_active && !data.usage_limit_reached,
  };
}

/**
 * The two gates that stand between a signed-in player and a turn — `is_active`, and the
 * spend budget (`docs/platform/limit-usage.md` §6). Independent, either refuses on its own,
 * and both are on the profile for the same reason the story budget is: so a control can be
 * absent with an explanation instead of the player typing a sentence into a 429.
 *
 * `reason` is a `UiKey` and not a sentence, so the two cases stay distinguishable —
 * "wait for a human" and "come back later" are different things to be told.
 */
export interface PlayGate {
  canPlay: boolean;
  reason: UiKey | null;
}

export function usePlayGate(): PlayGate | undefined {
  const { data } = useProfile();
  if (!data) return undefined;
  if (!data.is_active) return { canPlay: false, reason: 'ui.error.account_inactive' };
  if (data.usage_limit_reached) return { canPlay: false, reason: 'ui.error.usage_limit_reached' };
  return { canPlay: true, reason: null };
}

export function useUpdateLocale() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (locale: string) => request('patch', '/me', { body: { locale } }),
    onSuccess: async (profile: Profile) => {
      client.setQueryData(queryKeys.profile(), profile);
      // The UI language changed; the strings for it may not be cached yet.
      await client.invalidateQueries({ queryKey: ['translations'] });
    },
  });
}

// --- translations -----------------------------------------------------------------------

/**
 * D7: `en` is bundled at build time as the fallback, so a failed fetch degrades to English
 * rather than to empty strings. That bundling belongs to `src/i18n/`; this hook is only
 * the fetch, and it is cached on locale because a locale's strings never change within a
 * session.
 */
export function useTranslations(locale: string | undefined) {
  return useQuery({
    queryKey: queryKeys.translations(locale ?? ''),
    enabled: Boolean(locale),
    staleTime: Infinity,
    queryFn: ({ signal }) => {
      if (!locale) throw new Error('useTranslations called without a locale');
      return request('get', '/translations', { query: { locale }, signal });
    },
  });
}

// --- stories ----------------------------------------------------------------------------

export function useStories() {
  return useQuery({
    queryKey: queryKeys.stories(),
    queryFn: ({ signal }) => request('get', '/stories', { signal }),
    select: (data) => data.stories,
  });
}

/**
 * Split out of the hook because the turn machine fetches this query imperatively too: recovering
 * from a dropped stream means asking the server whether the turn landed, and that is
 * `client.fetchQuery(storyQueryOptions(id))` — the same key, the same fetch, no second
 * definition of either.
 */
export function storyQueryOptions(storyId: string) {
  return {
    queryKey: queryKeys.story(storyId),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      request('get', '/stories/{story_id}', { path: { story_id: storyId }, signal }),
  };
}

export function useStory(storyId: string | undefined) {
  return useQuery({
    ...storyQueryOptions(storyId ?? ''),
    enabled: Boolean(storyId),
  });
}

/** The modal-only half of the state (P1) — full world, display-joined (P8, U1, U3). */
export function useStoryState(storyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.storyState(storyId ?? ''),
    enabled: Boolean(storyId),
    queryFn: ({ signal }) => {
      if (!storyId) throw new Error('useStoryState called without a storyId');
      return request('get', '/stories/{story_id}/state', { path: { story_id: storyId }, signal });
    },
  });
}

/**
 * Upward infinite scroll, on the `before` cursor. `next_before: null` means the page
 * held chapter 1 — TanStack reads that as "no more pages" and `hasNextPage` goes false.
 */
export function useChapters(storyId: string | undefined) {
  return useInfiniteQuery({
    queryKey: queryKeys.chapters(storyId ?? ''),
    enabled: Boolean(storyId),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage: ChapterPage) => lastPage.next_before,
    queryFn: ({ pageParam, signal }) => {
      if (!storyId) throw new Error('useChapters called without a storyId');
      return request('get', '/stories/{story_id}/chapters', {
        path: { story_id: storyId },
        query:
          pageParam === null
            ? { limit: CHAPTER_PAGE_SIZE }
            : { before: pageParam, limit: CHAPTER_PAGE_SIZE },
        signal,
      });
    },
  });
}

export function useCreateStory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: StoryCreate) => request('post', '/stories', { body: input }),
    onSuccess: async (_created: StoryCreated) => {
      // stories_used moved, so the budget the "new story" control reads moved with it (P7).
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.stories() }),
        client.invalidateQueries({ queryKey: queryKeys.profile() }),
      ]);
    },
  });
}

export function useDeleteStory() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (storyId: string) =>
      request('delete', '/stories/{story_id}', { path: { story_id: storyId } }),
    onSuccess: async (_result, storyId) => {
      client.removeQueries({ queryKey: queryKeys.story(storyId) });
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.stories() }),
        client.invalidateQueries({ queryKey: queryKeys.profile() }),
      ]);
    },
  });
}

// --- feedback ---------------------------------------------------------------------------

export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (input: FeedbackRequest) => request('post', '/feedback', { body: input }),
  });
}
